import {
  API_PATHS,
  AUTH_PATH_SEGMENTS,
  AUTH_REFRESH_PATH,
  CONFIG_PATH,
  COURSES_JOIN_STATUS_PATH,
  USERS_CURRENT_PATH
} from '@treneo/shared/constants';

export { AUTH_PATH_SEGMENTS, AUTH_REFRESH_PATH, CONFIG_PATH } from '@treneo/shared/constants';

export const CURRENT_USER_PATH = USERS_CURRENT_PATH;

export const AUTH_ENDPOINT_PATHS: readonly string[] = [
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOGIN}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.REGISTER}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOGOUT}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOGOUT_ALL}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.REFRESH}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOG_IN_GOOGLE}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOG_IN_LINKEDIN}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOG_IN_AND_JOIN}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.REGISTER_AND_JOIN}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.FORGOT_PASSWORD}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.RESET_PASSWORD}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.CHANGE_PASSWORD}`,
  API_PATHS.AUTH_CSRF,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.CHECK_EXPIRY}`
];

export const ALLOWED_WHEN_LOGOUT_PATHS: readonly string[] = [
  API_PATHS.AUTH_CSRF,
  API_PATHS.AUTH_LOGOUT,
  CONFIG_PATH,
  COURSES_JOIN_STATUS_PATH
];

export const ALLOWED_WHEN_REFRESH_FAILED_PATHS: readonly string[] = [
  ...ALLOWED_WHEN_LOGOUT_PATHS,
  AUTH_REFRESH_PATH
];

export const LOGIN_ENDPOINT_PATHS: readonly string[] = [
  API_PATHS.AUTH_LOG_IN,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOG_IN_GOOGLE}`,
  `${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOG_IN_LINKEDIN}`
];

const REFRESH_PATH = AUTH_REFRESH_PATH;
const LOGOUT_PATH = API_PATHS.AUTH_LOGOUT;
const REGISTER_PATH = API_PATHS.AUTH_REGISTER;

const getPathFromUrl = (url: string): string | null => {
  try {
    return new URL(url, globalThis.window.location.origin).pathname;
  } catch {
    return null;
  }
};

const isPathExactOrPrefix = (urlPath: string, path: string): boolean =>
  urlPath === path || urlPath.startsWith(`${path}/`);

type UrlPathMatchOptions = { asPathname?: boolean };

const isUrlPathMatch = (url: string, path: string, options: UrlPathMatchOptions = {}): boolean => {
  if (options.asPathname !== false) {
    const urlPath = getPathFromUrl(url);
    if (urlPath !== null) {
      return isPathExactOrPrefix(urlPath, path);
    }
  }

  return url.includes(path) && !url.includes(`${path}-`);
};

export const isAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) {
    return false;
  }

  const urlPath = getPathFromUrl(url);
  if (urlPath !== null) {
    return AUTH_ENDPOINT_PATHS.some(path => isPathExactOrPrefix(urlPath, path));
  }

  return AUTH_ENDPOINT_PATHS.some(path => url.includes(path) && !url.includes(`${path}-`));
};

export const isUrlAllowed = (url: string, allowedPaths: readonly string[]): boolean =>
  allowedPaths.some(path => url.includes(path));

export const isRefreshEndpoint = (url: string): boolean =>
  !!url && isUrlPathMatch(url, REFRESH_PATH);

export const isLogoutEndpoint = (url: string): boolean => !!url && isUrlPathMatch(url, LOGOUT_PATH);

export const isLoginEndpoint = (url: string): boolean => {
  if (!url) {
    return false;
  }

  const urlPath = getPathFromUrl(url);
  if (urlPath !== null) {
    return LOGIN_ENDPOINT_PATHS.some(path => isPathExactOrPrefix(urlPath, path));
  }

  return (
    (url.includes(API_PATHS.AUTH_LOG_IN) && !url.includes(`${API_PATHS.AUTH_LOG_IN}-`)) ||
    url.includes(`${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOG_IN_GOOGLE}`) ||
    url.includes(`${API_PATHS.AUTH_PREFIX}/${AUTH_PATH_SEGMENTS.LOG_IN_LINKEDIN}`)
  );
};

export const isRegistrationEndpoint = (url: string): boolean =>
  !!url && isUrlPathMatch(url, REGISTER_PATH);

export const isCurrentUserEndpoint = (url: string): boolean =>
  !!url && (url.includes(USERS_CURRENT_PATH) || url.endsWith('/current'));
